const entryCreate = require('./contentful/entry-create');
const EntryService = require('../services/EntryService').default;
const entryUpdate = require('./contentful/entry-update');
const block = require('./block');
const getSpace = require('./contentful/get-space');
const transformer = require('./transformers/campaign');
const linkResolve = require('./contentful/link-resolve');
const processError = require('./contentful/error-process');
const update = require('immutability-helper');


let entryService = null;

const gettingEntryService = () => {
  if (!entryService) {
    entryService = getSpace().then((space) => {
      return new EntryService(space, transformer);
    });
  }

  return entryService;
};

const createCampaign = (space, campaign, depth) => {
  return block.saveBlockArray([], campaign.bodytext, false).then((bodyText) => {
    const newCampaign = update(campaign, {
      bodytext: {
        $set: bodyText
      }
    });
    const data = transformer.internalToContentful(newCampaign);

    return entryCreate(space, 'campaign', data);
  }).then((entry) => {
    const data = entry.toPlainObject();
    return linkResolve(space, data, depth);
  }).then((data) => {
    return transformer.contentfulToInternal(data);
  });
};

const updateCampaign = (space, campaign, depth) => {
  return space.getEntry(campaign.id).catch(
    processError
  ).then((entry) => {
    const data = entry.toPlainObject();
    let currentBodytextLinks = [];

    if (data.fields.bodytext && data.fields.bodytext['nb-NO']) {
      currentBodytextLinks = data.fields.bodytext['nb-NO'];
    }

    return block.saveBlockArray(currentBodytextLinks, campaign.bodytext, false).then((newBodyText) => {
      const newCampaign = update(campaign, {
        bodytext: {
          $set: newBodyText
        }
      });
      const data = transformer.internalToContentful(newCampaign);

      return entryUpdate(entry, data);
    }).then((entry) => {
      const data = entry.toPlainObject();
      return linkResolve(space, data, depth);
    }).then((data) => {
      return transformer.contentfulToInternal(data);
    });
  });
};

module.exports = {
  all(depth = 0, order) {
    return getSpace().then((space) => {
      return space.getEntries({
        content_type: 'campaign', // eslint-disable-line
        order: order || '-sys.updatedAt',
        limit: 999
      }).catch(
        processError
      ).then((entries) => {
        const data = entries.toPlainObject();

        return linkResolve(space, data, depth).then((entries) => {
          return entries.items.map(transformer.contentfulToInternal);
        });
      });
    });
  },

  allActive(depth = 0, limit = 100) {
    const nowDate = new Date();
    const now = nowDate.toISOString();
    return getSpace().then((space) => {
      const entryParams = {
        'content_type': 'campaign',
        'sys.publishedVersion[exists]': true,
        'fields.startDate[lte]': now,
        'fields.expiryDate[gte]': now,
        'order': '-fields.priority,fields.expiryDate',
        'include': depth,
        'limit': limit,
        'sys.archivedAt[exists]': false
      };

      return space.getEntries(entryParams).catch(
        processError
      ).then((entries) => {
        const data = entries.toPlainObject();

        return linkResolve(space, data, depth).then((entries) => {
          return [entries.items.map(transformer.contentfulToInternal), entries.total];
        });
      });
    });
  },

  allByParams(depth = 0, offset = 0, limit = 100, params = {}, order) {
    return getSpace().then((space) => {
      const entryParams = {
        content_type: 'campaign', // eslint-disable-line
        order: order || '-sys.updatedAt',
        skip: offset,
        limit: limit
      };

      if (params.makeId) {
        entryParams['fields.makes.sys.id'] = params.makeId;
      }

      if (!params.includeArchived) {
        entryParams['sys.archivedAt[exists]'] = false;
      }

      return space.getEntries(entryParams).catch(
        processError
      ).then((entries) => {
        const data = entries.toPlainObject();

        return linkResolve(space, data, depth).then((entries) => {
          return [entries.items.map(transformer.contentfulToInternal), entries.total];
        });
      });
    });
  },

  createEmpty() {
    const expireDate = new Date();
    expireDate.setDate(expireDate.getDate() + 7);
    return {
      published: false,
      updated: false,
      draft: true,

      name: '',
      summary: null,
      url: '',
      type: 'newcar',
      intro: '',
      description: '',
      titleBackground: null,
      bodytext: [],

      listImage: null,
      mainImage: null,

      priority: 0,
      startDate: new Date(),
      expiryDate: expireDate,

      callToActionHeader: '',
      callToActionText: '',
      callToActionInfo: 'Registrer din interesse og vi kontakter deg så raskt som mulig',
      callToActionButtonText: '',
      callToActionForm: 'TestDrive',
      callToActionFormExt: null,

      dealers: [],
      variants: [],
      lease: null,
      model: null,
      makes: [],
      hideCarPreview: false,
      showCampaignOnModelPage: false,
      mfRotationOverride: false,

      // Included stuff (note, these fields are stored in a single field in contentful, see transformer)
      includedEquipments: [],
      includedFactoryEquipments: [],
      availableColors: []
    };
  },

  save(campaign, depth = 0) {
    return getSpace().then((space) => {
      if (campaign.id) {
        return updateCampaign(space, campaign, depth);
      }

      return createCampaign(space, campaign, depth);
    });
  },

  getById(...parameters) {
    return gettingEntryService().then((service) => service.getById(...parameters));
  },

  publish(...parameters) {
    return gettingEntryService().then((service) => service.publish(...parameters));
  },

  publishWithBlocks(campaign, depth) {
    const bodytextIdsToPublish = campaign.bodytext.reduce((ids, md) => {
      if (!md.published || md.updated) {
        ids.push(md.id);
      }

      return ids;
    }, []);

    if (!bodytextIdsToPublish.length) {
      return gettingEntryService().then((service) => service.publish(campaign.id, depth));
    }

    return block.publishBlocks(bodytextIdsToPublish).then(() => {
      return gettingEntryService().then((service) => service.publish(campaign.id, depth));
    });
  },

  unpublish(...parameters) {
    return gettingEntryService().then((service) => service.unpublish(...parameters));
  },

  archive(...parameters) {
    return gettingEntryService().then((service) => service.archive(...parameters));
  },

  unarchive(...parameters) {
    return gettingEntryService().then((service) => service.unarchive(...parameters));
  }
};
